import { Button, Modal, Text } from "@vericus/cadmus-ui";

import { create, useModal } from "@ebay/nice-modal-react";

import { useAppSelector } from "@/data/hooks";
import { selectIsConnected } from "@/features/authority";

import * as styles from "./ConnectionBar.css";
import { useRetrySaveCallback } from "./use-save-retry-callback";

/**
 * NiceModal controlled connection error modal.
 */
export const ConnectionErrorModal = create<{ onRetry?: () => void }>(() => {
  const modal = useModal();
  const isConnected = useAppSelector(selectIsConnected);
  const [onRetrySave, loading] = useRetrySaveCallback();
  return (
    <Modal.Root open={modal.visible}>
      <ConnectionErrorModalContent
        onClose={() => modal.remove()}
        onRetry={isConnected ? onRetrySave : undefined}
        isRetrying={loading}
      />
    </Modal.Root>
  );
});

interface Props {
  onClose: () => void;
  onRetry?: () => void;
  isRetrying?: boolean;
}

/**
 * Connection error modal showing an alert to ask the student to contact their
 * supervisor in a lock down exam setting. AND make a conscious decision to
 * continue working offline.
 */
export function ConnectionErrorModalContent(props: Props) {
  return (
    <Modal.Content className={styles.modalContent} withCloseButton={false}>
      <AlertFillIcon />
      <Modal.Title asChild>
        <Text kind="headingFour">
          No Internet Connection!
          <br />
          Please notify your supervisor
        </Text>
      </Modal.Title>
      <Modal.Description>
        <Text kind="bodyMd" color={"navy400"}>
          Do not refresh or reload the page to avoid losing your progress.
          <br />
          Please let the invigilator know while we try to restore the
          connection.
        </Text>
      </Modal.Description>
      <div className={styles.modalActions}>
        <Modal.Close asChild>
          <Button onClick={props.onClose}>Continue Offline</Button>
        </Modal.Close>
        {props.onRetry && (
          <Button
            kind="secondary"
            onClick={props.onRetry}
            disabled={props.isRetrying}
          >
            {props.isRetrying ? "Trying to reconnect..." : "Try Again Now"}
          </Button>
        )}
      </div>
    </Modal.Content>
  );
}

const AlertFillIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="49"
    height="48"
    viewBox="0 0 49 48"
    fill="none"
  >
    <path
      d="M25.9809 7.86479L42.2709 36.4054C42.421 36.6683 42.5 36.9666 42.5 37.2703C42.5 37.5739 42.421 37.8722 42.2709 38.1351C42.1208 38.3981 41.905 38.6164 41.645 38.7682C41.385 38.9201 41.0902 39 40.79 39H8.21001C7.90984 39 7.61495 38.9201 7.355 38.7682C7.09504 38.6164 6.87918 38.3981 6.72909 38.1351C6.57901 37.8722 6.5 37.5739 6.5 37.2703C6.5 36.9666 6.57902 36.6683 6.7291 36.4054L23.0191 7.86479C23.1692 7.60186 23.3851 7.38352 23.645 7.23172C23.905 7.07992 24.1998 7 24.5 7C24.8002 7 25.095 7.07992 25.355 7.23172C25.6149 7.38352 25.8308 7.60186 25.9809 7.86479ZM22.7899 30.3513V33.8108H26.2101V30.3513H22.7899ZM22.7899 18.2432V26.8919H26.2101V18.2432H22.7899Z"
      fill="#DB0025"
    />
  </svg>
);
